import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { JWPlayer } from './player/jw-player'
import { YouTubePlayer } from './player/yt-player'
import { AbstractPlayer } from './player/abstract-player'
import {
    IEmbedConfig,
    JWPlayerConfigEvent,
    VideoProviderShortType,
    VideoProviderType,
    YTPlayerConfig,
    YTPlayerConfigEvent
} from './types'
import { getEmbedConfig, setAdConfigTargeting } from './util'
import { YTPlayerVars } from './constants'

export class SinglePlayer extends ComponentBase {
    private featured: boolean
    private player: AbstractPlayer

    /**
     * Get fallbackElement.
     *
     * @return {Nullable<HTMLElement>}
     */
    private get fallbackElement(): Nullable<HTMLElement> {
        return this.getElement('.video-fallback')
    }

    public constructor(private element: HTMLElement) {
        super()
        const provider = element.dataset.provider || ''
        this.featured = element.dataset.type === 'featured'

        if (provider === VideoProviderType.JwPlayer) {
            void this.createJWPlayer()
        } else if (provider === VideoProviderType.YouTube) {
            void this.createYouTubePlayer()
        }
    }

    private createJWPlayer(): void {
        const videoId = this.element.dataset.media || ''
        const version = this.element.dataset.version || ''
        const events: JWPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onError: () => this.onError(),
            onPlay: () => this.onPlay()
        }

        this.player = new JWPlayer(
            this.element,
            videoId,
            events,
            this.featured,
            false,
            version
        )
    }

    private async createYouTubePlayer(): Promise<void> {
        const videoId = this.element.dataset.videoId || ''
        const channelId = this.element.dataset.channelId || ''
        const host = this.element.dataset.host || ''
        let embedConfig: IEmbedConfig = await getEmbedConfig(channelId, true)

        // add additional Ad targeting for Player Ad Request
        embedConfig = setAdConfigTargeting(embedConfig, {
            player: VideoProviderShortType.YouTube
        })

        // Player configs setting up analytics
        let option: YTPlayerConfig = {}

        // Player configs adding data to setup Featured video
        if (this.featured) {
            option = {
                ...option,
                embedConfig: embedConfig,
                height: '360',
                playerVars: {
                    ...YTPlayerVars,
                    origin: host || window.location.origin
                },
                videoId,
                width: '480'
            }
        }

        const events: YTPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onError: () => this.onError(),
            onStateChange: (event: YT.OnStateChangeEvent) => {
                if (event.data === YT.PlayerState.PLAYING) {
                    this.onPlay()
                }
            }
        }

        this.player = new YouTubePlayer(
            this.element,
            videoId,
            option,
            events,
            this.featured,
            false
        )
    }

    // Event Handlers
    private onReady(): void {
        if (this.featured) {
            this.setFeaturedVideoTitle()
            this.player.autoPlay()
        } else {
            this.player.unmute()
        }
    }

    /**
     * Due to Promise getter resolving before data travels between components.
     * can potentially be fixed with using Timeout on Promise or RXJS Observable.
     */
    private setFeaturedVideoTitle(): void {
        const title = this.player.title
        const titleEl = this.getElement('.control-heading--max')
        if (title && titleEl) {
            titleEl.innerHTML = title
        }
    }

    private onError(): void {
        if (this.featured) {
            if (this.fallbackElement) {
                this.fallbackElement.style.display = 'flex'
            }
        }
    }

    private onPlay(): void {
        this.player.pauseAllOther()
    }
}
